import { useEffect, useState, useRef } from "react";
import { Bell, BellRing } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";
import { api } from "@/lib/api";

const supported = typeof window !== "undefined" && "Notification" in window;

export default function ReminderMenu() {
  const [enabled, setEnabled] = useState(() => localStorage.getItem("reminder_enabled") === "1");
  const [time, setTime] = useState(() => localStorage.getItem("reminder_time") || "18:00");
  const timer = useRef(null);

  useEffect(() => { localStorage.setItem("reminder_time", time); }, [time]);

  useEffect(() => {
    if (!enabled || !supported) {
      if (timer.current) clearInterval(timer.current);
      return;
    }
    const check = async () => {
      if (Notification.permission !== "granted") return;
      const now = new Date();
      const hhmm = `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;
      const todayStr = now.toISOString().slice(0, 10);
      if (hhmm !== time) return;
      if (localStorage.getItem("reminder_last") === todayStr) return;
      localStorage.setItem("reminder_last", todayStr);
      try {
        const status = await api.dailyStatus();
        if (!status.completed) {
          new Notification("StudioAI 🔥", {
            body: "È ora della Sfida Giornaliera! Non perdere il tuo streak.",
          });
        }
      } catch { /* ignore */ }
    };
    timer.current = setInterval(check, 30000);
    check();
    return () => timer.current && clearInterval(timer.current);
  }, [enabled, time]);

  const toggle = async (v) => {
    if (v && supported && Notification.permission !== "granted") {
      const perm = await Notification.requestPermission();
      if (perm !== "granted") {
        toast.error("Permesso notifiche negato dal browser.");
        return;
      }
    }
    setEnabled(v);
    localStorage.setItem("reminder_enabled", v ? "1" : "0");
    if (v) {
      toast.success(`Promemoria attivo ogni giorno alle ${time}`);
    } else {
      localStorage.removeItem("reminder_last");
    }
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button data-testid="reminder-menu-button" variant="ghost" size="icon" className="rounded-xl" aria-label="Promemoria">
          {enabled ? <BellRing className="h-5 w-5 text-primary" /> : <Bell className="h-5 w-5" />}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72">
        <div className="space-y-4">
          <div>
            <h4 className="font-display font-bold text-sm">Promemoria Sfida</h4>
            <p className="text-xs text-muted-foreground mt-0.5">Ricevi una notifica giornaliera per non perdere lo streak.</p>
          </div>
          {!supported ? (
            <p className="text-xs text-destructive">Il tuo browser non supporta le notifiche.</p>
          ) : (
            <>
              <div className="flex items-center justify-between">
                <Label htmlFor="reminder-switch" className="text-sm cursor-pointer">Attiva promemoria</Label>
                <Switch id="reminder-switch" data-testid="reminder-toggle" checked={enabled} onCheckedChange={toggle} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="reminder-time" className="text-sm">Orario</Label>
                <Input
                  id="reminder-time"
                  data-testid="reminder-time-input"
                  type="time"
                  value={time}
                  onChange={(e) => setTime(e.target.value)}
                  className="h-9"
                />
              </div>
              <p className="text-[11px] text-muted-foreground leading-relaxed">
                Nota: la notifica arriva quando StudioAI è aperto in una scheda del browser.
              </p>
            </>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
